import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Course, SocialLink } from "@/types/course";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { 
  Loader2, 
  FileImage, 
  Video, 
  X, 
  Upload, 
  Mail, 
  Info, 
  FileQuestion, 
  FileText,
  Link,
  Settings,
  Plus
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { Separator } from "@/components/ui/separator";
import { FaqSection } from "./create/FaqSection";
import { TermsSection } from "./create/TermsSection";
import { SocialLinksSection } from "./create/SocialLinksSection";

interface CourseInfoTabProps {
  courseId: string;
}

export const CourseInfoTab = ({ courseId }: CourseInfoTabProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    show_ratings: false,
    welcomeVideoUrl: "",
    contactEmail: "",
    show_powered_by: true,
    terms: "",
  });

  // State for FAQ items
  const [faqItems, setFaqItems] = useState<Array<{ question: string; answer: string }>>([
    { question: "", answer: "" }
  ]);

  // State for social links
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([
    { platform: "website", username: "" }
  ]);

  // State for section visibility
  const [sectionStates, setSectionStates] = useState({
    basicInfo: true,
    media: true,
    advancedSettings: true,
    faq: false,
    terms: false,
    socialLinks: false,
  });

  // File handling states
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [welcomeVideoFile, setWelcomeVideoFile] = useState<File | null>(null);
  const [welcomeVideoName, setWelcomeVideoName] = useState<string | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

  const { data: course, refetch } = useQuery({
    queryKey: ['course-info', courseId],
    queryFn: async () => {
      if (!courseId) throw new Error("Course ID is required");
      
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single();
        
      if (error) throw error;
      return data as Course;
    }
  });

  useEffect(() => {
    if (course) {
      setFormData({
        title: course.title || "",
        description: course.description || "",
        price: course.price ? course.price.toString() : "",
        show_ratings: course.show_ratings || false,
        welcomeVideoUrl: "",
        contactEmail: course.contact_email || "",
        show_powered_by: true,
        terms: "",
      });

      // Set thumbnail preview if available
      if (course.thumbnail_url) {
        setThumbnailPreview(course.thumbnail_url);
      }

      // Set welcome video information if available
      if (course.welcome_video_url) {
        setWelcomeVideoName(course.welcome_video_url.split('/').pop());
        setVideoPreviewUrl(course.welcome_video_url);
      }
      
      // Parse additional_info if available
      if (course.additional_info) {
        try {
          const additionalInfo = JSON.parse(course.additional_info);
          
          // Set terms if available
          if (additionalInfo.terms) {
            setFormData(prev => ({ ...prev, terms: additionalInfo.terms }));
          }
          
          // Set FAQ items if available
          if (additionalInfo.faq && additionalInfo.faq.length > 0) {
            setFaqItems(additionalInfo.faq);
          }
          
          // Set social links if available
          if (additionalInfo.socialLinks && additionalInfo.socialLinks.length > 0) {
            setSocialLinks(additionalInfo.socialLinks);
          }
          
          // Set show_powered_by if available
          if (additionalInfo.show_powered_by !== undefined) {
            setFormData(prev => ({ ...prev, show_powered_by: additionalInfo.show_powered_by }));
          }
        } catch (error) {
          console.error("Error parsing additional_info:", error);
        }
      }
    }
  }, [course]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleToggleChange = (name: string) => {
    setFormData(prev => ({ ...prev, [name]: !prev[name as keyof typeof prev] }));
  };

  const toggleSection = (section: string) => {
    setSectionStates(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev]
    }));
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWelcomeVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (!file.type.startsWith("video/")) {
        toast.error("Please upload a video file");
        return;
      }
      
      if (file.size > 100 * 1024 * 1024) {
        toast.error("Video must be less than 100MB");
        return;
      }
      
      setWelcomeVideoFile(file);
      setWelcomeVideoName(file.name);
      
      const videoObjectUrl = URL.createObjectURL(file);
      setVideoPreviewUrl(videoObjectUrl);
    }
  };

  const handleFaqChange = (index: number, field: "question" | "answer", value: string) => {
    const updatedFaqs = [...faqItems];
    updatedFaqs[index][field] = value;
    setFaqItems(updatedFaqs);
  };

  const addFaqItem = () => {
    setFaqItems([...faqItems, { question: "", answer: "" }]);
  };

  const removeFaqItem = (index: number) => {
    if (faqItems.length > 1) {
      const updatedFaqs = [...faqItems];
      updatedFaqs.splice(index, 1);
      setFaqItems(updatedFaqs);
    }
  };

  const handleSocialLinkChange = (index: number, field: keyof SocialLink, value: string) => {
    const updatedLinks = [...socialLinks];
    updatedLinks[index] = {
      ...updatedLinks[index],
      [field]: value
    };
    setSocialLinks(updatedLinks);
  };

  const addSocialLink = () => {
    setSocialLinks([...socialLinks, { platform: "website", username: "" }]);
  };

  const removeSocialLink = (index: number) => {
    if (socialLinks.length > 1) {
      const updatedLinks = [...socialLinks];
      updatedLinks.splice(index, 1);
      setSocialLinks(updatedLinks);
    } else {
      setSocialLinks([{ platform: "website", username: "" }]);
    }
  };

  const clearThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const clearWelcomeVideo = () => {
    setWelcomeVideoFile(null);
    setWelcomeVideoName(null);
    setVideoPreviewUrl(null);
    if (videoPreviewUrl && videoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("You must be logged in to update a course");
        return;
      }

      let thumbnailUrl = course?.thumbnail_url || null;
      let welcomeVideoUrl = course?.welcome_video_url || null;
      
      if (thumbnailFile) {
        const fileExt = thumbnailFile.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `${session.user.id}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('course-thumbnails')
          .upload(filePath, thumbnailFile);
          
        if (uploadError) {
          throw new Error(`Error uploading thumbnail: ${uploadError.message}`);
        }
        
        const { data: urlData } = supabase.storage
          .from('course-thumbnails')
          .getPublicUrl(filePath);
          
        thumbnailUrl = urlData.publicUrl;
      } else if (!thumbnailPreview) {
        thumbnailUrl = null;
      }
      
      if (welcomeVideoFile) {
        const fileExt = welcomeVideoFile.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `${session.user.id}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('course-videos')
          .upload(filePath, welcomeVideoFile);
          
        if (uploadError) {
          throw new Error(`Error uploading welcome video: ${uploadError.message}`);
        }
        
        const { data: urlData } = supabase.storage
          .from('course-videos')
          .getPublicUrl(filePath);
          
        welcomeVideoUrl = urlData.publicUrl;
      } else if (!videoPreviewUrl) {
        welcomeVideoUrl = null;
      }

      const additionalInfo = JSON.stringify({
        terms: formData.terms,
        faq: faqItems.filter(item => item.question.trim() && item.answer.trim()),
        socialLinks: socialLinks.filter(link => link.username.trim()),
        show_powered_by: formData.show_powered_by,
        contactEmail: formData.contactEmail
      });

      const { error } = await supabase
        .from('courses')
        .update({
          title: formData.title,
          description: formData.description,
          price: formData.price ? parseFloat(formData.price) : null,
          show_ratings: formData.show_ratings,
          thumbnail_url: thumbnailUrl,
          welcome_video_url: welcomeVideoUrl,
          additional_info: additionalInfo,
          contact_email: formData.contactEmail
        })
        .eq('id', courseId);

      if (error) throw error;
      
      toast.success("Course information updated successfully");
      refetch();
    } catch (error: any) {
      console.error("Error updating course:", error);
      toast.error(error.message || "Failed to update course information");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Course Information</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-6 md:col-span-2">
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-500" />
                <h2 className="text-xl font-semibold">Basic Information</h2>
              </div>
              
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="title">
                    Course Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g. Complete JavaScript Bootcamp"
                    required
                    className="bg-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">
                    Course Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe your course and what students will learn..."
                    className="min-h-[120px] bg-white"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="price">Price (USD)</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="e.g. 49.99 (leave empty for free)"
                    className="bg-white"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter 0 or leave empty to make this course free
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="contactEmail"
                      name="contactEmail"
                      type="email"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="bg-white"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This email will be displayed to students for support inquiries
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mt-8">
                <FileImage className="h-5 w-5 text-green-500" />
                <h2 className="text-xl font-semibold">Media</h2>
              </div>

              <div className="space-y-6 bg-gray-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <Label>Course Thumbnail</Label>
                  <div className="mt-1 flex items-center">
                    {thumbnailPreview ? (
                      <div className="relative">
                        <img
                          src={thumbnailPreview}
                          alt="Course thumbnail"
                          className="object-cover h-48 w-full md:w-96 rounded-md"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-8 w-8 p-0"
                          onClick={clearThumbnail}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-md p-12 w-full bg-white">
                        <FileImage className="h-8 w-8 text-muted-foreground" />
                        <div className="text-center">
                          <Label
                            htmlFor="thumbnail"
                            className="text-primary cursor-pointer hover:underline"
                          >
                            Click to upload
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            PNG, JPG or WEBP (max. 5MB)
                          </p>
                        </div>
                        <Input
                          id="thumbnail"
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailChange}
                          className="hidden"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Welcome Video</Label>
                  
                  <div className="mt-2">
                    {videoPreviewUrl && welcomeVideoName ? (
                      <div className="flex items-center justify-between p-3 border rounded-md bg-white">
                        <div className="flex items-center space-x-3">
                          <Video className="h-5 w-5 text-muted-foreground" />
                          <span className="text-sm truncate max-w-xs">{welcomeVideoName}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearWelcomeVideo}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-md p-8 bg-white">
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <div className="text-center">
                          <Label
                            htmlFor="welcome-video"
                            className="text-primary cursor-pointer hover:underline"
                          >
                            Click to upload
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            MP4, WEBM or MOV (max. 100MB)
                          </p>
                        </div>
                        <Input
                          id="welcome-video"
                          type="file"
                          accept="video/*"
                          onChange={handleWelcomeVideoChange}
                          className="hidden"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-500" />
                <h2 className="text-xl font-semibold">Settings</h2>
              </div>
              
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between border-b pb-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="show_ratings">Show ratings and reviews</Label>
                    <p className="text-xs text-muted-foreground">
                      Display student ratings and reviews
                    </p>
                  </div>
                  <Switch
                    id="show_ratings"
                    checked={formData.show_ratings}
                    onCheckedChange={() => handleToggleChange('show_ratings')}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="show_powered_by">Show "Powered by Showee"</Label>
                    <p className="text-xs text-muted-foreground">
                      Display "Powered by Showee" footer text
                    </p>
                  </div>
                  <Switch
                    id="show_powered_by"
                    checked={formData.show_powered_by}
                    onCheckedChange={() => handleToggleChange('show_powered_by')}
                  />
                </div>
              </div>
              
              <div className="mt-8 flex items-center gap-2">
                <Link className="h-5 w-5 text-orange-500" />
                <h2 className="text-xl font-semibold">Social Links</h2>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-end mb-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={addSocialLink}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Link
                  </Button>
                </div>
                
                {socialLinks.map((link, index) => (
                  <div key={index} className="space-y-3 p-3 border rounded-md mb-3 bg-white">
                    <div className="flex items-center justify-between">
                      <select
                        value={link.platform}
                        onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                        className="text-sm font-medium bg-transparent border-none focus:outline-none focus:ring-0"
                      >
                        <option value="website">Website</option>
                        <option value="twitter">Twitter</option>
                        <option value="facebook">Facebook</option>
                        <option value="instagram">Instagram</option>
                        <option value="youtube">YouTube</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="github">GitHub</option>
                      </select>
                      
                      {socialLinks.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSocialLink(index)}
                          className="h-7 w-7 p-0 text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <Input
                      value={link.username}
                      onChange={(e) => handleSocialLinkChange(index, 'username', e.target.value)}
                      placeholder={
                        link.platform === 'website' 
                          ? 'https://example.com' 
                          : `${link.platform} username or URL`
                      }
                      className="text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-4 border-t">
            <div className="flex items-center gap-2 mb-4">
              <FileQuestion className="h-5 w-5 text-indigo-500" />
              <h2 className="text-xl font-semibold">Frequently Asked Questions</h2>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-end mb-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addFaqItem}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add FAQ
                </Button>
              </div>

              {faqItems.map((item, index) => (
                <div key={index} className="space-y-3 p-4 border rounded-md mb-4 bg-white">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">FAQ #{index + 1}</h4>
                    {faqItems.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFaqItem(index)}
                        className="h-7 w-7 p-0 text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor={`faq-question-${index}`}>Question</Label>
                    <Input
                      id={`faq-question-${index}`}
                      placeholder="e.g. What will I learn in this course?"
                      value={item.question}
                      onChange={(e) => handleFaqChange(index, "question", e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor={`faq-answer-${index}`}>Answer</Label>
                    <Textarea
                      id={`faq-answer-${index}`}
                      placeholder="Provide a detailed answer to the question..."
                      value={item.answer}
                      onChange={(e) => handleFaqChange(index, "answer", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-teal-500" />
              <h2 className="text-xl font-semibold">Terms of Service</h2>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <Textarea
                  id="terms"
                  name="terms"
                  placeholder="Add any specific terms, conditions, or policies for this course..."
                  value={formData.terms}
                  onChange={handleChange}
                  className="min-h-[120px] bg-white"
                />
                <p className="text-xs text-muted-foreground">
                  These terms will be displayed to students before they enroll in your course.
                </p>
              </div>
            </div>
          </div>
          
          <div className="pt-4 sticky bottom-0 bg-white pb-4 border-t mt-6">
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Changes...
                </>
              ) : (
                "Save All Changes"
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
